/**
 * InboxPanel — the FULL-HEIGHT LIVE inbox overlay (the confirm-loop wave,
 * Ori 2026-07-25). Mounted by Chrome in the left-rail region as its OWN
 * overlay — sci//feed:@binary:@inbox XOR sci//feed:@binary:@tab** (the inbox
 * and the tab strip never share the rail; both-open is impossible by
 * construction, one word world-side).
 *
 * Renders the ONE channel store's merged inbox view: the SERVED world list
 * (keyed by ref, humanized labels, served lucide icon words) folded together
 * with the LOCAL work records (kind "status", local-first — the arrangement
 * reconcile trues them up against world truth). TWO filter boxes in Ori's
 * order — Notify | Status — ONE selected at a time, NOTIFY default
 * and NOTIFY alone carries the count tag (status is there but never
 * counted). The list stays COMPLETE (§34, never a data collapse). Entry
 * click opens its target through the ONE existing open path
 * (workspace.setActiveConversationId + ch.ensureConversation) and writes
 * the seen claim for world entries.
 */
import React, { useMemo, useState } from "react";

import type { InboxEntryKind } from "./client";
import { useConversationWorkspace } from "./ConversationWorkspace";
import { feedStore, useFeedStoreState, type WorkRecord } from "./feedStore";
import {
  inboxEntrySeen,
  inboxSeenAnnounce,
  lucideIconOfWord,
  statusWordOf,
} from "./feedWave";
import type { ChannelApi } from "./useChannel";

const timeLabel = (timestamp: string): string => {
  const s = String(timestamp || "");
  const v = Date.parse(/[zZ]$|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  if (!Number.isFinite(v)) return "";
  const minutes = Math.max(0, Math.round((Date.now() - v) / 60_000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

const epochOf = (timestamp: string): number => {
  const s = String(timestamp || "");
  const v = Date.parse(/[zZ]$|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return Number.isFinite(v) ? v : 0;
};

/** One rendered inbox row — a served world entry or a local work projection. */
type PanelEntry = {
  key: string;
  kind: InboxEntryKind;
  icon: string;
  label: string[];
  timestamp: string;
  conversation?: string;
  unseen: boolean;
  /** the world row edge (seen-write ref) — absent on local work records */
  worldRef?: number;
};

const isTerminal = (record: WorkRecord): boolean =>
  record.status === "complete" || record.status.startsWith("fail:");

//: the TWO kind boxes — NOTIFY leads and is the default (Ori's order,
//: the confirm-loop wave: notify · status; the "All" box is wiped;
//: one box selected at a time). Only the Notify box carries a count tag.
const FILTERS: Array<{ id: InboxEntryKind; label: string }> = [
  { id: "notify", label: "Notify" },
  { id: "status", label: "Status" },
];

export const InboxPanel: React.FC<{ ch: ChannelApi }> = ({ ch }) => {
  const workspace = useConversationWorkspace();
  const feed = useFeedStoreState();
  const [filter, setFilter] = useState<InboxEntryKind>("notify");
  const userId = ch.userId;
  const inbox = feed.inbox;

  const entries = useMemo<PanelEntry[]>(() => {
    const world: PanelEntry[] = (inbox?.list ?? []).map((entry) => ({
      key: `ref:${entry.ref}`,
      kind: entry.kind,
      icon: entry.icon,
      label: entry.label,
      timestamp: entry.timestamp,
      ...(entry.conversation ? { conversation: entry.conversation } : {}),
      unseen: !inboxEntrySeen(entry, inbox?.seen ?? []),
      worldRef: entry.ref,
    }));
    // local work folds in LOCAL-FIRST as the status lane (the reconcile
    // trues terminal records up against the world-authored list).
    const local: PanelEntry[] = feed.work.map((record) => ({
      key: record.id,
      kind: "status",
      icon: "",
      label: [record.label, statusWordOf(record.status)],
      timestamp: record.timestamp,
      ...(record.conversationId ? { conversation: record.conversationId } : {}),
      unseen: isTerminal(record) && !record.seen,
    }));
    return [...local, ...world].sort((x, y) => epochOf(y.timestamp) - epochOf(x.timestamp));
  }, [feed.work, inbox?.list, inbox?.seen]);

  const visible = entries.filter((entry) => entry.kind === filter);

  const openEntry = (entry: PanelEntry) => {
    // ONE open path: the entry's target conversation opens through the
    // shared workspace activation + context backfill.
    if (entry.conversation) {
      workspace.setActiveConversationId(entry.conversation, entry.label[0]);
      void ch.ensureConversation(entry.conversation).catch((error) => {
        workspace.setWorkspaceError(error instanceof Error ? error.message : String(error));
      });
    }
    if (!userId) return;
    // seeing the entry is seeing the change: seen-write the unseen world
    // entry (keyed per ref, idempotent world-side).
    if (!entry.unseen || entry.worldRef === undefined) return;
    const ref = String(entry.worldRef);
    const stamp = new Date().toISOString().replace(/Z$/, "");
    void ch.announce(inboxSeenAnnounce(userId, ref, stamp))
      .then(() => feedStore.applyInboxSeen([ref], stamp))
      .catch((error) => console.error("[inbox] the seen write failed (never reached the world):", error));
  };

  return (
    <aside className="conversation-section workspace-section" data-edge="left" data-inbox-panel aria-label="Inbox">
      <div style={filterRow} role="tablist" aria-label="Inbox filters">
        {FILTERS.map((f) => {
          const active = filter === f.id;
          // THE COUNT TAG rides ONLY the Notify box (Ori's rule) — the
          // unseen NOTIFY entries; status is there but never counted.
          const notifyCount = f.id === "notify"
            ? entries.filter((entry) => entry.kind === "notify" && entry.unseen).length
            : 0;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-inbox-filter={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                ...filterBox,
                background: active ? "var(--color-surface)" : "transparent",
                color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              }}
            >
              {f.label}
              {notifyCount > 0 && (
                <span
                  data-notify-count
                  style={{
                    marginLeft: 5, minWidth: 14, height: 14, borderRadius: 7,
                    padding: "0 3px", fontSize: 9, fontWeight: 700,
                    lineHeight: "14px", textAlign: "center", display: "inline-block",
                    background: "var(--accent-primary, #6B7EEA)", color: "#fff",
                  }}
                >
                  {notifyCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="workspace-section-scroll" style={listWrap} aria-busy={feed.arrangementLoading}>
        {feed.arrangementError && (
          <div className="workspace-section-state" data-kind="error" title={feed.arrangementError}>
            {feed.arrangementError}
          </div>
        )}
        {!feed.arrangementError && visible.length === 0 && (
          <div className="workspace-section-state" data-kind="empty">Nothing in your inbox yet.</div>
        )}
        {visible.map((entry) => {
          const unseen = entry.unseen;
          const Icon = lucideIconOfWord(entry.icon);
          return (
            <button
              key={entry.key}
              type="button"
              data-inbox-item
              data-kind={entry.kind}
              data-seen={unseen ? "false" : "true"}
              onClick={() => openEntry(entry)}
              style={{
                ...entryRow,
                background: unseen ? "var(--color-surface, #f8f9fa)" : "transparent",
              }}
              title={entry.label.join(" — ")}
            >
              <span style={entryIcon} aria-hidden="true">
                {Icon
                  ? <Icon size={14} />
                  : <span style={{ fontSize: 9, fontWeight: 700 }}>{(entry.kind || "?").charAt(0).toUpperCase()}</span>}
              </span>
              <span style={{ display: "grid", gap: 2, minWidth: 0, flex: 1 }}>
                <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{
                    fontSize: 11.5, fontWeight: unseen ? 700 : 500,
                    color: "var(--color-text-primary, #111827)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0,
                  }}>
                    {entry.label[0] || "Update"}
                  </span>
                  <span style={{ fontSize: 9.5, color: "var(--color-text-secondary, #9ca3af)", flexShrink: 0 }}>
                    {timeLabel(entry.timestamp)}
                  </span>
                </span>
                {entry.label.slice(1).map((line, index) => (
                  <span
                    key={index}
                    style={{
                      fontSize: 10.5, color: "var(--color-text-secondary, #6b7280)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {line}
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
};

const filterRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 4,
  padding: "10px 10px 6px",
  flexShrink: 0,
};
const filterBox: React.CSSProperties = {
  height: 26,
  border: "1px solid var(--color-border)",
  borderRadius: 7,
  cursor: "pointer",
  fontSize: 10.5,
  fontWeight: 650,
  padding: 0,
};
const listWrap: React.CSSProperties = {
  display: "grid",
  gap: 4,
  alignContent: "start",
  padding: "4px 8px",
  overflowY: "auto",
  minHeight: 0,
};
const entryRow: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  textAlign: "left",
  cursor: "pointer",
  border: "none",
  borderRadius: 8,
  padding: "7px 9px",
};
const entryIcon: React.CSSProperties = {
  display: "grid",
  placeItems: "center",
  width: 22,
  height: 22,
  borderRadius: 6,
  flexShrink: 0,
  color: "var(--color-text-secondary)",
  background: "var(--color-bg, transparent)",
  border: "1px solid var(--color-border)",
};
